import { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import blankProfileImg from '../media/blank_profile_img.webp';
import Base from './Base';
import Swal from 'sweetalert2';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import { Card, Button, Input, Textarea, Avatar, Spinner, Alert } from './ui';
import API_BASE_URL from '../config/api';

const UserProfile = () => {
  const location = useLocation();
  const { authToken } = useContext(AuthContext);

  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    contact_number: '',
    gender: 'Male',
    image: null,
    description: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/user_profile/`, {
          headers: {
            Authorization: `Bearer ${authToken.access}`,
          },
        });
        console.log(response.data.obj)
        setUserProfile(response.data.obj);
        setFormData({
          username: response.data.obj.username || '',
          email: response.data.obj.email || '',
          contact_number: response.data.obj.contact_number || '',
          gender: response.data.obj.gender || 'Male',
          image: response.data.obj.image || null,
          description: response.data.description || '',
        });
      } catch (err) {
        console.error('Failed to fetch profile:', err);
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [authToken]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const updatedData = new FormData();
    updatedData.append('username', formData.username);
    updatedData.append('email', formData.email);
    updatedData.append('contact_number', formData.contact_number);
    updatedData.append('gender', formData.gender);
    updatedData.append('description', formData.description);
    if (formData.image instanceof File) {
      updatedData.append('image', formData.image);
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/user_profile/`, updatedData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${authToken.access}`,
        },
      });
      setUserProfile(response.data.obj);
      setFormData(prev => ({
        ...prev,
        image: response.data.obj.image || null,
      }));
      Swal.fire({
        title: "Profile!",
        text: "Profile updated successfully!",
        icon: "success"
      });
    } catch (err) {
      console.error('Profile update failed:', err);
      setError('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Base
      Content={
        <div className="font-lora min-h-screen  py-10 px-5 md:px-32">
          {loading ? (
            <div className="text-center mt-20 text-xl font-semibold text-orange-700">
              Loading profile...
            </div>
          ) : (
            <>
              <div className="text-center mb-10">
                <h3 className="text-4xl font-bold text-[#d45614] drop-shadow-sm">Your Account</h3>
                <p className="text-xl text-orange-700 mt-2 italic">{userProfile?.username}</p>
              </div>

              <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-lg p-8 border border-orange-200">
                <h2 className="text-center text-3xl font-semibold text-[#d45614] mb-6">
                  Edit Profile
                </h2>

                <div className="flex justify-center mb-6">
                  <div className="w-40 h-40 rounded-full border-4 border-orange-500 overflow-hidden shadow-md">
                    <img
                      src={
                        formData?.image instanceof File
                          ? URL.createObjectURL(formData.image)
                          : formData?.image
                            ? `${API_BASE_URL}${formData.image}`
                            : blankProfileImg
                      } alt="Brand Settings"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="mb-5">
                    <label className="block font-bold text-lg text-orange-500 mb-1">
                      Upload image
                    </label>
                    <input
                      type="file"
                      name="image"
                      onChange={handleChange}
                      className="w-full text-sm border border-orange-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>

                  <div className="mb-6">
                    <label htmlFor="description" className="block text-lg font-medium mb-2">
                      Tell us about yourself <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      maxLength="1000"
                      rows={4}
                      className="w-full p-3 border border-gray-300 rounded bg-[#fffaf3] text-lg resize-y focus:outline-none focus:ring-2 focus:ring-orange-400"
                      value={formData.description}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block font-bold text-lg text-orange-500 mb-1">Username</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className="w-full p-3 border border-orange-300 rounded focus:ring-2 focus:ring-orange-400"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block font-bold text-lg text-orange-500 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full p-3 border border-orange-300 rounded focus:ring-2 focus:ring-orange-400"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block font-bold text-lg text-orange-500 mb-1">
                      Contact Number
                    </label>
                    <input
                      type="text"
                      name="contact_number"
                      value={formData.contact_number}
                      onChange={handleChange}
                      className="w-full p-3 border border-orange-300 rounded focus:ring-2 focus:ring-orange-400"
                    />
                  </div>

                  <div className="mb-5">
                    <label className="block font-bold text-lg text-orange-500 mb-1">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full p-3 border border-orange-300 rounded focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  {error && (
                    <p className="text-red-600 text-center mb-4 font-medium">{error}</p>
                  )}

                  <div className="flex justify-center">
                    <button
                      type="submit"
                      disabled={saving}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold px-6 py-3 rounded-xl shadow-md hover:shadow-lg hover:from-orange-600 hover:to-orange-700 transition duration-200 ease-in-out"
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};

export default UserProfile;
